/**
 * TagInputComponent
 * Tag input control for managing comma/space-separated values
 */

import React, { useCallback, useMemo, useState, memo } from 'react';
import type { FormControlProps } from '../../types';

interface TagInputComponentProps extends FormControlProps {
  placeholder?: string;
  defaultTags?: string[];
  allowDuplicates?: boolean;
  separator?: 'comma' | 'space' | 'enter';
  maxTags?: number;
  minTagLength?: number;
  maxTagLength?: number;
  allowSpaces?: boolean;
  onChange?: (tags: string[]) => void;
}

export const TagInputComponent: React.FC<TagInputComponentProps> = memo(({
  value = [],
  placeholder = 'Enter tags...',
  defaultTags = [],
  allowDuplicates = false,
  separator = 'comma',
  maxTags = 0,
  minTagLength = 1,
  maxTagLength = 50,
  allowSpaces = true,
  onChange,
  disabled = false,
  required = false,
  label,
  error,
  helpText,
}) => {
  const inputId = useMemo(
    () => `tag-input-${label?.replace(/\s+/g, '-').toLowerCase()}`,
    [label],
  );

  const tags = useMemo(() => {
    if (Array.isArray(value)) {
      return value as string[];
    }
    return defaultTags;
  }, [value, defaultTags]);

  const [inputValue, setInputValue] = useState('');

  const isValidTag = useCallback(
    (tag: string): boolean => {
      const trimmed = tag.trim();
      if (!trimmed) return false;
      if (trimmed.length < minTagLength || trimmed.length > maxTagLength) return false;
      if (!allowDuplicates && tags.includes(trimmed)) return false;
      return true;
    },
    [tags, minTagLength, maxTagLength, allowDuplicates],
  );

  const addTag = useCallback(
    (tag: string) => {
      const trimmed = !allowSpaces ? tag.trim() : tag;
      if (!isValidTag(trimmed)) return;
      if (maxTags > 0 && tags.length >= maxTags) return;

      const newTags = [...tags, trimmed];
      onChange?.(newTags);
      setInputValue('');
    },
    [tags, isValidTag, maxTags, allowSpaces, onChange],
  );

  const removeTag = useCallback(
    (index: number) => {
      const newTags = tags.filter((_, i) => i !== index);
      onChange?.(newTags);
    },
    [tags, onChange],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.currentTarget.value;
      setInputValue(val);

      // Auto-add tag on separator
      if (separator === 'comma' && val.includes(',')) {
        const tag = val.slice(0, -1);
        addTag(tag);
      } else if (separator === 'space' && val.endsWith(' ')) {
        const tag = val.trim();
        addTag(tag);
      }
    },
    [separator, addTag],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (separator === 'enter' && e.key === 'Enter') {
        e.preventDefault();
        addTag(inputValue);
      } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
        removeTag(tags.length - 1);
      }
    },
    [separator, inputValue, tags, addTag, removeTag],
  );

  return (
    <div className="af-form-field__group tag-input-group">
      {label && (
        <label className="af-form-field__label">
          {label}
          {required && <span className="af-form-field__required">*</span>}
        </label>
      )}

      <div className="tag-input__container">
        <div className="tag-input__tags">
          {tags.map((tag, index) => (
            <span key={index} className="tag-input__tag">
              {tag}
              <button
                type="button"
                onClick={() => removeTag(index)}
                className="tag-input__tag-remove"
                disabled={disabled}
                aria-label={`Remove tag: ${tag}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>

        <input
          id={inputId}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={tags.length < (maxTags || Infinity) ? placeholder : 'Max tags reached'}
          disabled={disabled || (maxTags > 0 && tags.length >= maxTags)}
          className="tag-input__input"
          aria-label={label || 'Tag input'}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : helpText ? `${inputId}-help` : undefined}
        />
      </div>

      {maxTags > 0 && (
        <p className="tag-input__counter">
          {tags.length} / {maxTags}
        </p>
      )}

      {error && (
        <div id={`${inputId}-error`} className="af-form-field__error">
          {error}
        </div>
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-form-field__help">
          {helpText}
        </div>
      )}
    </div>
  );
});
